import { ACTIONS, CHECK_FUNCTIONS, cardState, checkDocument, createDirectiveRegistry, createStateStore, defineSchema, dispatchCardAction, evaluateVisibility, executeAction, flattenToPointers, getByPath, handledActions, immutableSetByPath, isDevtoolsActive, isEmailAddress, isRecord, isRunNotCompleted, isSpec, nextActionDispatchId, notifyActionDispatch, notifyActionSettle, passEmptyPatterns, placeholderFor, resolveAction, resolveActionParam, resolveBindings, resolveElementProps, resolveRepeatItemStatePath, resolveRepeatStatePath, rowDeclaresChecks, runValidation, splitRepeatVisibility, subscribeDevtoolsActive, updatePromptDocument, viewFor, withoutUnhandledActions } from "../state-DnYzYGfm.js";
import React2, { Component, createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
defineSchema((s) => ({
	spec: s.object({
		/** Root element key */
		root: s.string(),
		/** Flat map of elements by key */
		elements: s.record(s.object({
			/** Component type from catalog */
			type: s.ref("catalog.components"),
			/** Component props */
			props: s.propsOf("catalog.components"),
			/** Child element keys (flat reference) */
			children: s.array(s.string()),
			slots: {
				...s.record(s.array(s.string())),
				...s.optional()
			},
			/** Visibility condition */
			visible: {
				...s.any(),
				...s.optional()
			},
			/** Repeat children from a state array */
			repeat: {
				...s.any(),
				...s.optional()
			}
		}))
	}),
	catalog: s.object({
		/** Component definitions */
		components: s.map({
			/** Zod schema for component props */
			props: s.zod(),
			/** Slots for this component. Use ['default'] for children, or named slots like ['header', 'footer'] */
			slots: s.array(s.string()),
			/** Description for AI generation hints */
			description: s.string(),
			/** Example prop values used in prompt examples (auto-generated from Zod schema if omitted) */
			example: s.any()
		}),
		/** Action definitions (optional) */
		actions: s.map({
			/** Zod schema for action params */
			params: s.zod(),
			/** Description for AI generation hints */
			description: s.string()
		})
	})
}), {
	builtInActions: [
		{
			name: "setState",
			description: "Update a value in the state model at the given statePath. Params: { statePath: string, value: any }"
		},
		{
			name: "pushState",
			description: "Append an item to an array in state. Params: { statePath: string, value: any, clearStatePath?: string }. Value can contain {\"$state\":\"/path\"} refs and \"$id\" for auto IDs."
		},
		{
			name: "removeState",
			description: "Remove an item from an array in state by index. Params: { statePath: string, index: number }"
		},
		{
			name: "validateForm",
			description: "Validate all registered form fields and write the result to state. Params: { statePath?: string }. Defaults to /formValidation. Result: { valid: boolean, errors: Record<string, string[]> }."
		}
	],
	defaultRules: [
		"CRITICAL INTEGRITY CHECK: Before outputting ANY element that references children, you MUST have already output (or will output) each child as its own element. If an element has children: ['a', 'b'], then elements 'a' and 'b' MUST exist. A missing child element causes that entire branch of the UI to be invisible.",
		"SELF-CHECK: After generating all elements, mentally walk the tree from root. Every key in every children array must resolve to a defined element. If you find a gap, output the missing element immediately.",
		"REQUIRED FIELDS: Every element MUST include a \"children\" array. Leaf elements (text, badges, inputs, images) use an empty array: \"children\": []. Omitting \"children\" fails validation.",
		"NAMED SLOTS: Use \"children\" for the default slot. For other slots declared by the component, use a top-level \"slots\" object that maps each slot name to child element keys, for example {\"slots\":{\"header\":[\"heading\"],\"footer\":[\"actions\"]}}. Never use \"slots.default\". Every referenced key must exist.",
		"FILTERED LISTS: To render only the items matching a field value (kanban columns, tabbed lists, status sections), put \"repeat\" and a \"visible\" condition with $item on the same container element: {\"repeat\": {\"statePath\": \"/tasks\", \"key\": \"id\"}, \"visible\": {\"$item\": \"status\", \"eq\": \"todo\"}} renders one child per matching item. A visible condition object must use exactly one of $state, $item, or $index — never combine them in one object.",
		"CRITICAL: The \"visible\" field goes on the ELEMENT object, NOT inside \"props\". Correct: {\"type\":\"<ComponentName>\",\"props\":{},\"visible\":{\"$state\":\"/tab\",\"eq\":\"home\"},\"children\":[...]}.",
		"CRITICAL: The \"on\" field goes on the ELEMENT object, NOT inside \"props\". Use on.press, on.change, on.submit etc. NEVER put action/actionParams inside props.",
		"When the user asks for a UI that displays data (e.g. blog posts, products, users), ALWAYS include a state field with realistic sample data. The state field is a top-level field on the spec (sibling of root/elements).",
		"When building repeating content backed by a state array (e.g. posts, products, items), use the \"repeat\" field on a container element. Example: { \"type\": \"<ContainerComponent>\", \"props\": {}, \"repeat\": { \"statePath\": \"/posts\", \"key\": \"id\" }, \"children\": [\"post-card\"] }. For a nested list stored on the enclosing item, use \"repeat\": { \"statePath\": { \"$item\": \"comments\" }, \"key\": \"id\" }. The $item statePath form is valid only inside another repeat. Replace <ContainerComponent> with an appropriate component from the AVAILABLE COMPONENTS list. Inside repeated children, use { \"$item\": \"field\" } to read a field from the current item, and { \"$index\": true } for the current array index. For two-way binding to an item field use { \"$bindItem\": \"completed\" }. Do NOT hardcode individual elements for each array item.",
		"Design with visual hierarchy: use container components to group content, heading components for section titles, proper spacing, and status indicators. ONLY use components from the AVAILABLE COMPONENTS list.",
		"For data-rich UIs, use multi-column layout components if available. For forms and single-column content, use vertical layout components. ONLY use components from the AVAILABLE COMPONENTS list.",
		"Always include realistic, professional-looking sample data. For blogs include 3-4 posts with varied titles, authors, dates, categories. For products include names, prices, images. Never leave data empty."
	]
});
//#endregion
//#region ../../node_modules/@json-render/react/dist/index.mjs
var StateContext = createContext(null);
function computeInitialFlat(isControlled, initialState) {
	if (isControlled) return null;
	if (Object.keys(initialState).length === 0) return {};
	return flattenToPointers(initialState);
}
function StateProvider({ store: externalStore, initialState = {}, onStateChange, children }) {
	const internalStoreRef = useRef(void 0);
	if (!externalStore && !internalStoreRef.current) internalStoreRef.current = createStateStore(initialState);
	const store = externalStore ?? internalStoreRef.current;
	const storeRef = useRef(store);
	storeRef.current = store;
	const isControlledRef = useRef(!!externalStore);
	isControlledRef.current = !!externalStore;
	const initialModeRef = useRef(externalStore ? "controlled" : "uncontrolled");
	const modeWarnedRef = useRef(false);
	if (process.env.NODE_ENV !== "production") {
		const currentMode = externalStore ? "controlled" : "uncontrolled";
		if (currentMode !== initialModeRef.current && !modeWarnedRef.current) {
			modeWarnedRef.current = true;
			console.warn(`StateProvider: switching from ${initialModeRef.current} to ${currentMode} mode is not supported.`);
		}
	}
	const prevInitialStateRef = useRef(initialState);
	const prevFlatRef = useRef(computeInitialFlat(!!externalStore, initialState));
	useEffect(() => {
		if (externalStore) return;
		if (initialState === prevInitialStateRef.current) return;
		prevInitialStateRef.current = initialState;
		const nextFlat = initialState && Object.keys(initialState).length > 0 ? flattenToPointers(initialState) : {};
		const prevFlat = prevFlatRef.current ?? {};
		const allKeys = /* @__PURE__ */ new Set([...Object.keys(prevFlat), ...Object.keys(nextFlat)]);
		const updates = {};
		for (const key of allKeys) if (prevFlat[key] !== nextFlat[key]) updates[key] = key in nextFlat ? nextFlat[key] : void 0;
		prevFlatRef.current = nextFlat;
		if (Object.keys(updates).length > 0) store.update(updates);
	}, [
		externalStore,
		initialState,
		store
	]);
	const state = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getServerSnapshot ?? store.getSnapshot);
	const onStateChangeRef = useRef(onStateChange);
	onStateChangeRef.current = onStateChange;
	const set = useCallback((path, value2) => {
		const s = storeRef.current;
		const prev = s.getSnapshot();
		s.set(path, value2);
		if (!isControlledRef.current && s.getSnapshot() !== prev) onStateChangeRef.current?.([{
			path,
			value: value2
		}]);
	}, []);
	const update = useCallback((updates) => {
		const s = storeRef.current;
		const prev = s.getSnapshot();
		s.update(updates);
		if (!isControlledRef.current && s.getSnapshot() !== prev) {
			const changes = [];
			for (const [path, value2] of Object.entries(updates)) if (getByPath(prev, path) !== value2) changes.push({
				path,
				value: value2
			});
			if (changes.length > 0) onStateChangeRef.current?.(changes);
		}
	}, []);
	const get = useCallback((path) => storeRef.current.get(path), []);
	const getSnapshot = useCallback(() => storeRef.current.getSnapshot(), []);
	const value = useMemo(() => ({
		state,
		get,
		set,
		update,
		getSnapshot
	}), [
		state,
		get,
		set,
		update,
		getSnapshot
	]);
	return /* @__PURE__ */ jsx(StateContext.Provider, {
		value,
		children
	});
}
function useStateStore() {
	const ctx = useContext(StateContext);
	if (!ctx) throw new Error("useStateStore must be used within a StateProvider");
	return ctx;
}
function useStateValue(path) {
	const { state } = useStateStore();
	return getByPath(state, path);
}
var VisibilityContext = createContext(null);
function VisibilityProvider({ children }) {
	const { state } = useStateStore();
	const ctx = useMemo(() => ({ stateModel: state }), [state]);
	const isVisible = useMemo(() => (condition) => evaluateVisibility(condition, ctx), [ctx]);
	const value = useMemo(() => ({
		isVisible,
		ctx
	}), [isVisible, ctx]);
	return /* @__PURE__ */ jsx(VisibilityContext.Provider, {
		value,
		children
	});
}
function useVisibility() {
	const ctx = useContext(VisibilityContext);
	if (!ctx) throw new Error("useVisibility must be used within a VisibilityProvider");
	return ctx;
}
var ValidationContext = createContext(null);
var EMPTY_VALIDATION_FUNCTIONS = {};
function dynamicArgsEqual(a, b) {
	if (a === b) return true;
	if (!a || !b) return false;
	const keysA = Object.keys(a);
	const keysB = Object.keys(b);
	if (keysA.length !== keysB.length) return false;
	for (const key of keysA) {
		const va = a[key];
		const vb = b[key];
		if (va === vb) continue;
		if (typeof va === "object" && va !== null && typeof vb === "object" && vb !== null) {
			const sa = va.$state;
			const sb = vb.$state;
			if (typeof sa === "string" && sa === sb) continue;
		}
		return false;
	}
	return true;
}
function validationConfigEqual(a, b) {
	if (a === b) return true;
	if (a.validateOn !== b.validateOn) return false;
	const ac = a.checks ?? [];
	const bc = b.checks ?? [];
	if (ac.length !== bc.length) return false;
	for (let i = 0; i < ac.length; i++) {
		const ca = ac[i];
		const cb = bc[i];
		if (ca.type !== cb.type) return false;
		if (ca.message !== cb.message) return false;
		if (!dynamicArgsEqual(ca.args, cb.args)) return false;
	}
	return true;
}
function ValidationProvider({ customFunctions = EMPTY_VALIDATION_FUNCTIONS, children }) {
	const { state, getSnapshot } = useStateStore();
	const [fieldStates, setFieldStates] = useState({});
	const fieldStatesRef = useRef({});
	const [fieldConfigs, setFieldConfigs] = useState({});
	const registerField = useCallback((path, config) => {
		setFieldConfigs((prev) => {
			const existing = prev[path];
			if (existing && validationConfigEqual(existing, config)) return prev;
			return {
				...prev,
				[path]: config
			};
		});
	}, []);
	const validate = useCallback((path, config) => {
		const currentState = getSnapshot();
		const segments = path.split("/").filter(Boolean);
		let value2 = currentState;
		for (const seg of segments) if (value2 != null && typeof value2 === "object") value2 = value2[seg];
		else {
			value2 = void 0;
			break;
		}
		const result = runValidation(config, {
			value: value2,
			stateModel: currentState,
			customFunctions
		});
		const newFieldState = {
			touched: fieldStatesRef.current[path]?.touched ?? true,
			validated: true,
			result
		};
		fieldStatesRef.current = {
			...fieldStatesRef.current,
			[path]: newFieldState
		};
		setFieldStates(fieldStatesRef.current);
		return result;
	}, [customFunctions, getSnapshot]);
	const touch = useCallback((path) => {
		fieldStatesRef.current = {
			...fieldStatesRef.current,
			[path]: {
				...fieldStatesRef.current[path],
				touched: true,
				validated: fieldStatesRef.current[path]?.validated ?? false,
				result: fieldStatesRef.current[path]?.result ?? null
			}
		};
		setFieldStates(fieldStatesRef.current);
	}, []);
	const clear = useCallback((path) => {
		const { [path]: _, ...rest } = fieldStatesRef.current;
		fieldStatesRef.current = rest;
		setFieldStates(rest);
	}, []);
	const validateAll = useCallback(() => {
		let allValid = true;
		for (const [path, config] of Object.entries(fieldConfigs)) if (!validate(path, config).valid) allValid = false;
		return allValid;
	}, [fieldConfigs, validate]);
	const value = useMemo(() => ({
		customFunctions,
		get fieldStates() {
			return fieldStatesRef.current;
		},
		validate,
		touch,
		clear,
		validateAll,
		registerField
	}), [
		customFunctions,
		fieldStates,
		validate,
		touch,
		clear,
		validateAll,
		registerField
	]);
	return /* @__PURE__ */ jsx(ValidationContext.Provider, {
		value,
		children
	});
}
function useValidation() {
	const ctx = useContext(ValidationContext);
	if (!ctx) throw new Error("useValidation must be used within a ValidationProvider");
	return ctx;
}
function useOptionalValidation() {
	return useContext(ValidationContext);
}
function useFieldValidation(path, config) {
	const { fieldStates, validate: validateField, touch: touchField, clear: clearField, registerField } = useValidation();
	React2.useEffect(() => {
		if (path && config) registerField(path, config);
	}, [
		path,
		config,
		registerField
	]);
	const state = fieldStates[path] ?? {
		touched: false,
		validated: false,
		result: null
	};
	return {
		state,
		validate: useCallback(() => validateField(path, config ?? { checks: [] }), [
			path,
			config,
			validateField
		]),
		touch: useCallback(() => touchField(path), [path, touchField]),
		clear: useCallback(() => clearField(path), [path, clearField]),
		errors: state.result?.errors ?? [],
		isValid: state.result?.valid ?? true
	};
}
var idCounter = 0;
function generateUniqueId() {
	idCounter += 1;
	return `${Date.now()}-${idCounter}`;
}
function deepResolveValue(value, get) {
	if (value === null || value === void 0) return value;
	if (value === "$id") return generateUniqueId();
	if (typeof value === "object" && !Array.isArray(value)) {
		const obj = value;
		const keys = Object.keys(obj);
		if (keys.length === 1 && typeof obj.$state === "string") return get(obj.$state);
		if (keys.length === 1 && "$id" in obj) return generateUniqueId();
	}
	if (Array.isArray(value)) return value.map((item) => deepResolveValue(item, get));
	if (typeof value === "object") {
		const resolved = {};
		for (const [key, val] of Object.entries(value)) resolved[key] = deepResolveValue(val, get);
		return resolved;
	}
	return value;
}
var ActionContext = createContext(null);
function ActionProvider({ handlers: initialHandlers = {}, navigate, children }) {
	const { get, set, getSnapshot } = useStateStore();
	const validation = useOptionalValidation();
	const [handlers, setHandlers] = useState(initialHandlers);
	const [loadingActions, setLoadingActions] = useState(/* @__PURE__ */ new Set());
	const [pendingConfirmation, setPendingConfirmation] = useState(null);
	const registerHandler = useCallback((name, handler) => {
		setHandlers((prev) => ({
			...prev,
			[name]: handler
		}));
	}, []);
	const execute = useCallback(async (binding) => {
		const resolved = resolveAction(binding, getSnapshot());
		const dispatchId = nextActionDispatchId();
		const dispatchedAt = Date.now();
		notifyActionDispatch({
			id: dispatchId,
			name: resolved.action,
			params: resolved.params,
			at: dispatchedAt
		});
		let __ok = true;
		let __error = void 0;
		try {
			if (resolved.action === "setState" && resolved.params) {
				const statePath = resolved.params.statePath;
				const value2 = resolved.params.value;
				if (statePath) set(statePath, value2);
				return;
			}
			if (resolved.action === "pushState" && resolved.params) {
				const statePath = resolved.params.statePath;
				const rawValue = resolved.params.value;
				if (statePath) {
					const resolvedValue = deepResolveValue(rawValue, get);
					const arr = get(statePath) ?? [];
					set(statePath, [...arr, resolvedValue]);
					const clearStatePath = resolved.params.clearStatePath;
					if (clearStatePath) set(clearStatePath, "");
				}
				return;
			}
			if (resolved.action === "removeState" && resolved.params) {
				const statePath = resolved.params.statePath;
				const index = resolved.params.index;
				if (statePath !== void 0 && index !== void 0) {
					const arr = get(statePath) ?? [];
					set(statePath, arr.filter((_, i) => i !== index));
				}
				return;
			}
			if (resolved.action === "push" && resolved.params) {
				const screen = resolved.params.screen;
				if (screen) {
					const currentScreen = get("/currentScreen");
					const navStack = get("/navStack") ?? [];
					if (currentScreen) set("/navStack", [...navStack, currentScreen]);
					else set("/navStack", [...navStack, ""]);
					set("/currentScreen", screen);
				}
				return;
			}
			if (resolved.action === "pop") {
				const navStack = get("/navStack") ?? [];
				if (navStack.length > 0) {
					const previousScreen = navStack[navStack.length - 1];
					set("/navStack", navStack.slice(0, -1));
					if (previousScreen) set("/currentScreen", previousScreen);
					else set("/currentScreen", void 0);
				}
				return;
			}
			if (resolved.action === "validateForm") {
				const validateAll = validation?.validateAll;
				if (!validateAll) {
					console.warn("validateForm action was dispatched but no ValidationProvider is connected. Ensure ValidationProvider is rendered inside the provider tree.");
					return;
				}
				const valid = validateAll();
				const errors = {};
				for (const [path, fs] of Object.entries(validation.fieldStates)) if (fs.result && !fs.result.valid) errors[path] = fs.result.errors;
				const statePath = resolved.params?.statePath || "/formValidation";
				set(statePath, {
					valid,
					errors
				});
				return;
			}
			const handler = handlers[resolved.action];
			if (!handler) {
				console.warn(`No handler registered for action: ${resolved.action}`);
				return;
			}
			if (resolved.confirm) return new Promise((resolve, reject) => {
				setPendingConfirmation({
					action: resolved,
					handler,
					resolve: () => {
						setPendingConfirmation(null);
						resolve();
					},
					reject: () => {
						setPendingConfirmation(null);
						reject(/* @__PURE__ */ new Error("Action cancelled"));
					}
				});
			}).then(async () => {
				setLoadingActions((prev) => new Set(prev).add(resolved.action));
				try {
					await executeAction({
						action: resolved,
						handler,
						setState: set,
						navigate,
						executeAction: async (binding2) => {
							await execute(binding2);
						}
					});
				} finally {
					setLoadingActions((prev) => {
						const next = new Set(prev);
						next.delete(resolved.action);
						return next;
					});
				}
			});
			setLoadingActions((prev) => new Set(prev).add(resolved.action));
			try {
				await executeAction({
					action: resolved,
					handler,
					setState: set,
					navigate,
					executeAction: async (binding2) => {
						await execute(binding2);
					}
				});
			} finally {
				setLoadingActions((prev) => {
					const next = new Set(prev);
					next.delete(resolved.action);
					return next;
				});
			}
		} catch (err) {
			__ok = false;
			__error = err;
			throw err;
		} finally {
			const now = Date.now();
			notifyActionSettle({
				id: dispatchId,
				name: resolved.action,
				ok: __ok,
				at: now,
				durationMs: now - dispatchedAt,
				error: __error
			});
		}
	}, [
		handlers,
		get,
		set,
		getSnapshot,
		navigate,
		validation
	]);
	const confirm = useCallback(() => {
		pendingConfirmation?.resolve();
	}, [pendingConfirmation]);
	const cancel = useCallback(() => {
		pendingConfirmation?.reject();
	}, [pendingConfirmation]);
	const value = useMemo(() => ({
		handlers,
		loadingActions,
		pendingConfirmation,
		execute,
		confirm,
		cancel,
		registerHandler
	}), [
		handlers,
		loadingActions,
		pendingConfirmation,
		execute,
		confirm,
		cancel,
		registerHandler
	]);
	return /* @__PURE__ */ jsx(ActionContext.Provider, {
		value,
		children
	});
}
function useActions() {
	const ctx = useContext(ActionContext);
	if (!ctx) throw new Error("useActions must be used within an ActionProvider");
	return ctx;
}
function ConfirmDialog({ confirm, onConfirm, onCancel }) {
	const isDanger = confirm.variant === "danger";
	return /* @__PURE__ */ jsx("div", {
		style: {
			position: "fixed",
			inset: 0,
			backgroundColor: "rgba(0, 0, 0, 0.5)",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			zIndex: 50
		},
		onClick: onCancel,
		children: /* @__PURE__ */ jsxs("div", {
			style: {
				backgroundColor: "white",
				borderRadius: "8px",
				padding: "24px",
				maxWidth: "400px",
				width: "100%",
				boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)"
			},
			onClick: (e) => e.stopPropagation(),
			children: [
				/* @__PURE__ */ jsx("h3", {
					style: {
						margin: "0 0 8px 0",
						fontSize: "18px",
						fontWeight: 600
					},
					children: confirm.title
				}),
				/* @__PURE__ */ jsx("p", {
					style: {
						margin: "0 0 24px 0",
						color: "#6b7280"
					},
					children: confirm.message
				}),
				/* @__PURE__ */ jsxs("div", {
					style: {
						display: "flex",
						gap: "12px",
						justifyContent: "flex-end"
					},
					children: [/* @__PURE__ */ jsx("button", {
						onClick: onCancel,
						style: {
							padding: "8px 16px",
							borderRadius: "6px",
							border: "1px solid #d1d5db",
							backgroundColor: "white",
							cursor: "pointer"
						},
						children: confirm.cancelLabel ?? "Cancel"
					}), /* @__PURE__ */ jsx("button", {
						onClick: onConfirm,
						style: {
							padding: "8px 16px",
							borderRadius: "6px",
							border: "none",
							backgroundColor: isDanger ? "#dc2626" : "#3b82f6",
							color: "white",
							cursor: "pointer"
						},
						children: confirm.confirmLabel ?? "Confirm"
					})]
				})
			]
		})
	});
}
var RepeatScopeContext = createContext(null);
function RepeatScopeProvider({ item, index, basePath, children }) {
	return /* @__PURE__ */ jsx(RepeatScopeContext.Provider, {
		value: {
			item,
			index,
			basePath
		},
		children
	});
}
function useRepeatScope() {
	return useContext(RepeatScopeContext);
}
var registryMetadata = /* @__PURE__ */ new WeakMap();
var EMPTY_ELEMENT_PROPS = {};
var ElementErrorBoundary = class extends React2.Component {
	constructor(props) {
		super(props);
		this.state = { hasError: false };
	}
	static getDerivedStateFromError() {
		return { hasError: true };
	}
	componentDidCatch(error, info) {
		console.error(`[json-render] Rendering error in <${this.props.elementType}>:`, error, info.componentStack);
	}
	componentDidUpdate(previous) {
		if (this.state.hasError && previous.resetKey !== this.props.resetKey) this.setState({ hasError: false });
	}
	render() {
		if (this.state.hasError) return null;
		return this.props.children;
	}
};
var EMPTY_FUNCTIONS = {};
var FunctionsContext = React2.createContext(EMPTY_FUNCTIONS);
function useFunctions() {
	return React2.useContext(FunctionsContext);
}
var DirectivesContext = React2.createContext(void 0);
function useDirectives() {
	return React2.useContext(DirectivesContext);
}
function stabilizeRecord(value, ref) {
	const previous = ref.current;
	if (previous === value) return previous;
	if (previous && value) {
		const keys = Object.keys(value);
		if (keys.length === Object.keys(previous).length && keys.every((key) => Object.prototype.hasOwnProperty.call(previous, key) && previous[key] === value[key])) return previous;
	}
	ref.current = value;
	return value;
}
function isPlainRecord(value) {
	if (value === null || typeof value !== "object") return false;
	const prototype = Object.getPrototypeOf(value);
	return prototype === Object.prototype || prototype === null;
}
function structurallyEqual(previous, next) {
	if (Object.is(previous, next)) return true;
	const previousIsArray = Array.isArray(previous);
	if (previousIsArray !== Array.isArray(next)) return false;
	if (!previousIsArray && (previous === null || next === null || typeof previous !== "object" || typeof next !== "object")) return false;
	const previousRecord = previous;
	const nextRecord = next;
	const keys = Object.keys(nextRecord);
	if (previousIsArray && previous.length !== next.length || keys.length !== Object.keys(previousRecord).length) return false;
	return keys.every((key) => Object.prototype.hasOwnProperty.call(previousRecord, key) && structurallyEqual(previousRecord[key], nextRecord[key]));
}
function snapshotStructuralValue(value) {
	if (value === null || typeof value !== "object") return value;
	const snapshot = Array.isArray(value) ? new Array(value.length) : {};
	for (const key of Object.keys(value)) snapshot[key] = snapshotStructuralValue(value[key]);
	return snapshot;
}
function shareResolvedValue(previous, next) {
	if (Object.is(previous, next)) return previous;
	const previousIsArray = Array.isArray(previous);
	if (previousIsArray !== Array.isArray(next)) return next;
	if (!previousIsArray && (!isPlainRecord(previous) || !isPlainRecord(next))) return next;
	const previousRecord = previous;
	const nextRecord = next;
	const keys = Object.keys(nextRecord);
	const shared = previousIsArray ? new Array(next.length) : {};
	let unchanged = (!previousIsArray || previous.length === next.length) && keys.length === Object.keys(previousRecord).length;
	for (const key of keys) {
		let value = nextRecord[key];
		if (Object.prototype.hasOwnProperty.call(previousRecord, key)) {
			value = shareResolvedValue(previousRecord[key], value);
			if (!Object.is(previousRecord[key], value)) unchanged = false;
		} else unchanged = false;
		shared[key] = value;
	}
	return unchanged ? previous : shared;
}
function useElementSignatures(spec) {
	const entriesRef = useRef({});
	const versionRef = useRef(0);
	if (!spec) return {};
	const previous = entriesRef.current;
	const next = {};
	const signatures = {};
	const visiting = /* @__PURE__ */ new Set();
	for (const key of Object.keys(spec.elements)) {
		if (signatures[key] !== void 0) continue;
		const element = spec.elements[key];
		if (!element) continue;
		visiting.add(key);
		const stack = [{
			key,
			element,
			children: [...element.children ?? [], ...Object.values(element.slots ?? {}).flat()],
			childIndex: 0,
			childVersions: []
		}];
		while (stack.length > 0) {
			const frame = stack[stack.length - 1];
			const childKey = frame.children[frame.childIndex];
			if (childKey !== void 0) {
				const childVersion = signatures[childKey];
				if (childVersion !== void 0) {
					frame.childVersions.push([childKey, childVersion]);
					frame.childIndex += 1;
					continue;
				}
				const childElement = spec.elements[childKey];
				if (!childElement || visiting.has(childKey)) {
					frame.childVersions.push([childKey, -1]);
					frame.childIndex += 1;
					continue;
				}
				visiting.add(childKey);
				stack.push({
					key: childKey,
					element: childElement,
					children: [...childElement.children ?? [], ...Object.values(childElement.slots ?? {}).flat()],
					childIndex: 0,
					childVersions: []
				});
				continue;
			}
			const prior = previous[frame.key];
			const version = prior && structurallyEqual(prior.own, frame.element) && structurallyEqual(prior.children, frame.childVersions) ? prior.version : ++versionRef.current;
			visiting.delete(frame.key);
			next[frame.key] = {
				own: snapshotStructuralValue(frame.element),
				children: frame.childVersions,
				version
			};
			signatures[frame.key] = version;
			stack.pop();
		}
	}
	entriesRef.current = next;
	return signatures;
}
var CatalogComponentBoundary = React2.memo(function CatalogComponentBoundary2({ Component, element, slots, emit, on, bindings, loading, children }) {
	return /* @__PURE__ */ jsx(Component, {
		element,
		slots,
		emit,
		on,
		bindings,
		loading,
		children
	});
}, (previous, next) => previous.Component === next.Component && previous.signature === next.signature && previous.execute === next.execute && previous.actionContext?.item === next.actionContext?.item && previous.actionContext?.index === next.actionContext?.index && previous.actionContext?.basePath === next.actionContext?.basePath && previous.functions === next.functions && previous.directives === next.directives && previous.element.props === next.element.props && previous.bindings === next.bindings && previous.loading === next.loading);
function useDevtoolsActive() {
	return React2.useSyncExternalStore(subscribeDevtoolsActive, isDevtoolsActive, () => false);
}
function ReactiveElementRenderer({ element, elementKey, spec, registry, loading, fallback, signatures }) {
	const devtoolsActive = useDevtoolsActive();
	const repeatScope = useRepeatScope();
	const { ctx } = useVisibility();
	const { execute } = useActions();
	const { getSnapshot, state: watchState } = useStateStore();
	const functions = useFunctions();
	const directives = useDirectives();
	const fullCtx = useMemo(() => {
		const base = repeatScope ? {
			...ctx,
			repeatItem: repeatScope.item,
			repeatIndex: repeatScope.index,
			repeatBasePath: repeatScope.basePath
		} : { ...ctx };
		base.functions = functions;
		base.directives = directives;
		return base;
	}, [
		ctx,
		repeatScope,
		functions,
		directives
	]);
	const repeatVisibility = element.repeat !== void 0 && repeatScope == null ? splitRepeatVisibility(element.visible) : {
		container: element.visible,
		itemFilter: void 0
	};
	const repeatItemFilter = repeatVisibility.itemFilter;
	const isVisible = repeatVisibility.container === void 0 ? true : evaluateVisibility(repeatVisibility.container, fullCtx);
	const onBindings = element.on;
	const emit = useCallback(async (eventName) => {
		const binding = onBindings?.[eventName];
		if (!binding) return;
		const actionBindings = Array.isArray(binding) ? binding : [binding];
		for (const b of actionBindings) {
			if (!b.params) {
				await execute(b);
				continue;
			}
			const liveCtx = {
				...fullCtx,
				stateModel: getSnapshot()
			};
			const resolved = {};
			for (const [key, val] of Object.entries(b.params)) resolved[key] = resolveActionParam(val, liveCtx);
			await execute({
				...b,
				params: resolved
			});
		}
	}, [
		onBindings,
		execute,
		fullCtx,
		getSnapshot
	]);
	const on = useCallback((eventName) => {
		const binding = onBindings?.[eventName];
		if (!binding) return {
			emit: () => {},
			shouldPreventDefault: false,
			bound: false
		};
		return {
			emit: () => emit(eventName),
			shouldPreventDefault: (Array.isArray(binding) ? binding : [binding]).some((b) => b.preventDefault),
			bound: true
		};
	}, [onBindings, emit]);
	const watchConfig = element.watch;
	const prevWatchValues = useRef(null);
	const stableWatchRef = useRef(void 0);
	const stableBindingsRef = useRef(void 0);
	const stableResolvedPropsRef = useRef({});
	const watchedValues = useMemo(() => {
		if (!watchConfig) return void 0;
		const values = {};
		for (const path of Object.keys(watchConfig)) values[path] = getByPath(watchState, path);
		const prev = stableWatchRef.current;
		if (prev) {
			const keys = Object.keys(values);
			if (keys.length === Object.keys(prev).length && keys.every((k) => values[k] === prev[k])) return prev;
		}
		stableWatchRef.current = values;
		return values;
	}, [watchConfig, watchState]);
	useEffect(() => {
		if (!watchConfig || !watchedValues) return;
		const paths = Object.keys(watchConfig);
		if (paths.length === 0) return;
		const prev = prevWatchValues.current;
		prevWatchValues.current = watchedValues;
		if (prev === null) return;
		let cancelled = false;
		(async () => {
			for (const path of paths) {
				if (cancelled) break;
				if (watchedValues[path] !== prev[path]) {
					const binding = watchConfig[path];
					if (!binding) continue;
					const bindings = Array.isArray(binding) ? binding : [binding];
					for (const b of bindings) {
						if (cancelled) break;
						if (!b.params) {
							await execute(b);
							if (cancelled) break;
							continue;
						}
						const liveCtx = {
							...fullCtx,
							stateModel: getSnapshot()
						};
						const resolved = {};
						for (const [key, val] of Object.entries(b.params)) resolved[key] = resolveActionParam(val, liveCtx);
						await execute({
							...b,
							params: resolved
						});
						if (cancelled) break;
					}
				}
			}
		})().catch(console.error);
		return () => {
			cancelled = true;
		};
	}, [
		watchConfig,
		watchedValues,
		execute,
		fullCtx,
		getSnapshot
	]);
	if (!isVisible) return null;
	const rawProps = element.props ?? EMPTY_ELEMENT_PROPS;
	const elementBindings = stabilizeRecord(resolveBindings(rawProps, fullCtx), stableBindingsRef);
	const resolvedProps = shareResolvedValue(stableResolvedPropsRef.current, resolveElementProps(rawProps, fullCtx));
	stableResolvedPropsRef.current = resolvedProps;
	const resolvedElement = resolvedProps !== element.props ? {
		...element,
		props: resolvedProps
	} : element;
	const Component = registry[resolvedElement.type] ?? fallback;
	if (!Component) {
		console.warn(`No renderer for component type: ${resolvedElement.type}`);
		return null;
	}
	const metadata = registryMetadata.get(registry)?.[resolvedElement.type];
	if (resolvedElement.slots && metadata?.slots) {
		const availableSlots = new Set(metadata.slots);
		for (const slotName of Object.keys(resolvedElement.slots)) if (slotName === "default") console.warn(`[json-render] Component "${resolvedElement.type}" uses slots.default. Use "children" for default slot content.`);
		else if (!availableSlots.has(slotName)) console.warn(`[json-render] Unknown slot "${slotName}" on component "${resolvedElement.type}". Available slots: ${metadata.slots.join(", ")}`);
	}
	const renderChildKeys = (childKeys, slotName) => childKeys.map((childKey) => {
		const childElement = spec.elements[childKey];
		if (!childElement) {
			if (!loading) {
				const location = slotName ? `in slot "${slotName}" of "${resolvedElement.type}"` : `as child of "${resolvedElement.type}"`;
				console.warn(`[json-render] Missing element "${childKey}" referenced ${location}. This element will not render.`);
			}
			return null;
		}
		return /* @__PURE__ */ jsx(ElementRenderer, {
			element: childElement,
			elementKey: childKey,
			spec,
			registry,
			loading,
			fallback,
			signatures
		}, childKey);
	});
	const children = resolvedElement.repeat ? /* @__PURE__ */ jsx(RepeatChildren, {
		element: resolvedElement,
		spec,
		registry,
		loading,
		fallback,
		itemFilter: repeatItemFilter,
		signatures
	}) : resolvedElement.children ? renderChildKeys(resolvedElement.children) : void 0;
	const slots = resolvedElement.slots ? Object.fromEntries(Object.entries(resolvedElement.slots).map(([slotName, childKeys]) => [slotName, renderChildKeys(childKeys, slotName)])) : void 0;
	const rendered = /* @__PURE__ */ jsx(CatalogComponentBoundary, {
		Component,
		signature: signatures[elementKey ?? ""],
		execute,
		actionContext: repeatScope,
		functions,
		directives,
		element: resolvedElement,
		slots,
		emit,
		on,
		bindings: elementBindings,
		loading,
		children
	});
	const tagged = devtoolsActive && elementKey ? /* @__PURE__ */ jsx("span", {
		"data-jr-key": elementKey,
		style: { display: "contents" },
		children: rendered
	}) : rendered;
	return /* @__PURE__ */ jsx(ElementErrorBoundary, {
		elementType: resolvedElement.type,
		resetKey: signatures[elementKey ?? ""],
		children: tagged
	});
}
var ElementRenderer = React2.memo(function ElementRenderer2(props) {
	return /* @__PURE__ */ jsx(ReactiveElementRenderer, { ...props });
}, (previous, next) => previous.elementKey === next.elementKey && previous.signatures[previous.elementKey ?? ""] === next.signatures[next.elementKey ?? ""] && previous.registry === next.registry && previous.loading === next.loading && previous.fallback === next.fallback);
function RepeatChildren({ element, spec, registry, loading, fallback, signatures, itemFilter }) {
	const { state } = useStateStore();
	const { ctx } = useVisibility();
	const parentScope = useRepeatScope();
	const repeat = element.repeat;
	const statePath = resolveRepeatStatePath(repeat.statePath, parentScope?.basePath);
	if (statePath === void 0) {
		console.warn("[json-render] $item in repeat.statePath used outside of a repeat scope");
		return null;
	}
	const entries = (getByPath(state, statePath) ?? []).map((itemValue, index) => ({
		itemValue,
		index
	})).filter(({ itemValue, index }) => itemFilter === void 0 || evaluateVisibility(itemFilter, {
		...ctx,
		repeatItem: itemValue,
		repeatIndex: index
	}));
	return /* @__PURE__ */ jsx(Fragment, { children: entries.map(({ itemValue, index }) => {
		const key = repeat.key && typeof itemValue === "object" && itemValue !== null ? String(itemValue[repeat.key] ?? index) : String(index);
		return /* @__PURE__ */ jsx(RepeatScopeProvider, {
			item: itemValue,
			index,
			basePath: resolveRepeatItemStatePath(statePath, index),
			children: element.children?.map((childKey) => {
				const childElement = spec.elements[childKey];
				if (!childElement) {
					if (!loading) console.warn(`[json-render] Missing element "${childKey}" referenced as child of "${element.type}" (repeat). This element will not render.`);
					return null;
				}
				return /* @__PURE__ */ jsx(ElementRenderer, {
					element: childElement,
					elementKey: childKey,
					spec,
					registry,
					loading,
					fallback,
					signatures
				}, childKey);
			})
		}, key);
	}) });
}
function Renderer({ spec, registry, loading, fallback }) {
	const signatures = useElementSignatures(spec);
	if (!spec || !spec.root) return null;
	const rootElement = spec.elements[spec.root];
	if (!rootElement) return null;
	return /* @__PURE__ */ jsx(ElementRenderer, {
		element: rootElement,
		elementKey: spec.root,
		spec,
		registry,
		loading,
		fallback,
		signatures
	});
}
function JSONUIProvider({ registry, store, initialState, handlers, navigate, validationFunctions, functions, directives, onStateChange, children }) {
	const directiveRegistry = useMemo(() => directives ? createDirectiveRegistry(directives) : void 0, [directives]);
	return /* @__PURE__ */ jsx(StateProvider, {
		store,
		initialState,
		onStateChange,
		children: /* @__PURE__ */ jsx(VisibilityProvider, { children: /* @__PURE__ */ jsx(ValidationProvider, {
			customFunctions: validationFunctions,
			children: /* @__PURE__ */ jsx(ActionProvider, {
				handlers,
				navigate,
				children: /* @__PURE__ */ jsx(FunctionsContext.Provider, {
					value: functions ?? EMPTY_FUNCTIONS,
					children: /* @__PURE__ */ jsxs(DirectivesContext.Provider, {
						value: directiveRegistry,
						children: [children, /* @__PURE__ */ jsx(ConfirmationDialogManager, {})]
					})
				})
			})
		}) })
	});
}
function ConfirmationDialogManager() {
	const { pendingConfirmation, confirm, cancel } = useActions();
	if (!pendingConfirmation?.action.confirm) return null;
	return /* @__PURE__ */ jsx(ConfirmDialog, {
		confirm: pendingConfirmation.action.confirm,
		onConfirm: confirm,
		onCancel: cancel
	});
}
function defineRegistry(catalog, options) {
	const registry = {};
	if (options.components) for (const [name, componentFn] of Object.entries(options.components)) registry[name] = ({ element, children, slots, emit, on, bindings, loading }) => {
		return componentFn({
			props: element.props,
			children,
			slots,
			emit,
			on,
			bindings,
			loading
		});
	};
	const catalogComponents = catalog.data.components;
	if (catalogComponents) registryMetadata.set(registry, catalogComponents);
	const actionMap = options.actions ? Object.entries(options.actions) : [];
	const handlers = (getSetState, getState) => {
		const result = {};
		for (const [name, actionFn] of actionMap) result[name] = async (params) => {
			const setState = getSetState();
			const state = getState();
			if (setState) await actionFn(params, setState, state);
		};
		return result;
	};
	const executeAction2 = async (actionName, params, setState, state = {}) => {
		const entry = actionMap.find(([name]) => name === actionName);
		if (entry) await entry[1](params, setState, state);
		else console.warn(`Unknown action: ${actionName}`);
	};
	return {
		registry,
		handlers,
		executeAction: executeAction2
	};
}
function useBoundProp(propValue, bindingPath) {
	const { set } = useStateStore();
	return [propValue, useCallback((value) => {
		if (bindingPath) set(bindingPath, value);
	}, [bindingPath, set])];
}
//#endregion
//#region src/react/CardErrorBoundary.tsx
var CardErrorBoundary = class CardErrorBoundary extends Component {
	state = {
		renderedKey: void 0,
		failed: false
	};
	static getDerivedStateFromError() {
		return { failed: true };
	}
	static getDerivedStateFromProps(props, state) {
		if (!state.failed) return { renderedKey: props.resetKey };
		if (state.renderedKey !== props.resetKey) return {
			failed: false,
			renderedKey: props.resetKey
		};
		return null;
	}
	componentDidCatch(error, info) {
		console.warn("[ACTION_CARDS] card failed to render", {
			error,
			componentStack: info.componentStack
		});
	}
	render() {
		if (!this.state.failed) return this.props.children;
		return /* @__PURE__ */ jsx(CardErrorBoundary, {
			placeholder: null,
			resetKey: this.props.resetKey,
			children: this.props.placeholder
		});
	}
};
//#endregion
//#region src/react/cardStore.ts
function createCardStore(seed) {
	const inner = createStateStore(seed);
	const editedPaths = /* @__PURE__ */ new Set();
	const store = {
		...inner,
		set(path, value) {
			recordEdit(path, value);
			inner.set(path, value);
		},
		update(updates) {
			for (const [path, value] of Object.entries(updates)) recordEdit(path, value);
			inner.update(updates);
		}
	};
	function recordEdit(path, value) {
		if (!isSameValue(inner.get(path), value)) editedPaths.add(path);
	}
	function isEdited(path) {
		return [...editedPaths].some((editedPath) => editedPath === path || editedPath.startsWith(`${path}/`) || path.startsWith(`${editedPath}/`));
	}
	function reseed(state) {
		const current = inner.getSnapshot();
		let next = state;
		for (const path of editedPaths) next = immutableSetByPath(next, path, getByPath(current, path));
		inner.update(topLevelPointers(next));
	}
	function uncommittedEdits() {
		return new Map([...editedPaths].map((path) => [path, inner.get(path)]));
	}
	function markCommitted(edits) {
		for (const [path, value] of edits) if (inner.get(path) === value) editedPaths.delete(path);
	}
	function fields() {
		const value = inner.get("/fields");
		return isRecord(value) ? value : {};
	}
	return {
		clearFieldError: (path) => clearFieldError(inner, path),
		store,
		reseed,
		uncommittedEdits,
		markCommitted,
		fields,
		isEdited
	};
}
function editedFields(edits, fields) {
	const keys = /* @__PURE__ */ new Set();
	for (const path of edits.keys()) {
		if (path === "/fields") return fields;
		if (path.startsWith("/fields/")) keys.add(path.slice(8).split("/")[0]);
	}
	return Object.fromEntries([...keys].filter((key) => key in fields).map((key) => [key, fields[key]]));
}
function clearFieldError(store, path) {
	if (path.startsWith("/fields/")) store.set(path.replace("/fields/", "/validation_errors/fields/"), void 0);
}
function topLevelPointers(state) {
	return Object.fromEntries(Object.entries(state).map(([key, value]) => [`/${key}`, value]));
}
function isSameValue(left, right) {
	if (Object.is(left, right)) return true;
	if (Array.isArray(left) && Array.isArray(right)) return left.length === right.length && left.every((item, index) => isSameValue(item, right[index]));
	if (isRecord(left) && isRecord(right)) {
		const keys = Object.keys(left);
		return keys.length === Object.keys(right).length && keys.every((key) => key in right && isSameValue(left[key], right[key]));
	}
	return false;
}
//#endregion
//#region src/react/cardSession.ts
function isRunning(run, row) {
	if (run === void 0) return false;
	if (row.status !== run.basis.status || !isSameValue(row.result, run.basis.result)) return false;
	return run.answeredAt === void 0 || run.answeredAt === row;
}
const ARMED_MS = 4e3;
function createCardSession(options) {
	let inputs = options.inputs;
	const latest = () => inputs;
	const cardStore = createCardStore(inputs.seed);
	const ui = createUiStore();
	const commit = createCommitQueue(cardStore, latest, options, ui);
	const arming = createArming(ui);
	const { validate, setValidator } = createValidation(ui, arming);
	const runNow = createRunNow(latest, commit, options, validate);
	const run = (key) => {
		if (validate()) runNow(key);
	};
	function press(action, params) {
		const { handlers, seed } = latest();
		arming.disarmAny();
		const result = dispatchCardAction({
			action,
			params
		}, {
			...handlers,
			run,
			open: (url) => handlers.open(url, commit)
		}, seed);
		if (!result.ok) console.warn("[ACTION_CARDS] press refused", {
			action,
			reason: result.reason
		});
	}
	return {
		setValidator,
		store: cardStore.store,
		ui: ui.get,
		subscribe: ui.subscribe,
		hasChecks: () => rowDeclaresChecks(latest().row.views),
		controls: {
			...createControls(ui, arming, commit),
			validate,
			fieldChanged: (path) => {
				clearFieldRefusal(cardStore, options, path);
			}
		},
		setInputs: (next) => {
			const rowChanged = next.row !== inputs.row;
			inputs = next;
			if (rowChanged) options.setRefusal((refusal) => keepRefusalsOfEditedFields(refusal, cardStore));
		},
		reseed: cardStore.reseed,
		commit,
		actionHandlers: createActionHandlers(press)
	};
}
function createActionHandlers(press) {
	return Object.fromEntries(ACTIONS.map((action) => [action, async (params) => press(action, params)]));
}
function createValidation(ui, arming) {
	let validator = () => true;
	return {
		setValidator: (next) => {
			validator = next;
		},
		validate: () => {
			if (validator()) return true;
			arming.disarmAny();
			ui.update({ invalidAttempts: (ui.get().invalidAttempts ?? 0) + 1 });
			return false;
		}
	};
}
function clearFieldRefusal(cardStore, options, path) {
	cardStore.clearFieldError(path);
	options.setRefusal((refusal) => clearRefusal(refusal, path));
}
function clearRefusal(refusal, path) {
	const key = path.slice(8);
	if (!refusal?.fields[key]) return refusal;
	const fields = { ...refusal.fields };
	delete fields[key];
	return {
		...refusal,
		fields
	};
}
function createControls(ui, arming, commit) {
	return {
		arm: arming.arm,
		disarm: arming.disarm,
		focus: (path) => ui.update({ focused: path }),
		blur: (path) => {
			if (ui.get().focused === path) ui.update({ focused: void 0 });
		},
		retrySave: () => void commit(),
		commitNow: () => void commit(),
		saveEdits: commit
	};
}
function createRunNow(latest, commit, { setRun, setRefusal }, validate) {
	async function runOnHost(key, seenSyncId) {
		try {
			const outcome = await latest().handlers.run(key, seenSyncId);
			if (isRunNotCompleted(outcome)) {
				setRefusal(void 0);
				return false;
			}
			setRefusal(outcome?.validation_errors);
			return outcome === void 0;
		} catch (error) {
			console.warn("[ACTION_CARDS] run refused", {
				key,
				error
			});
			return false;
		}
	}
	return async (key) => {
		if (!validate()) return;
		const basis = latest().row;
		setRun({ basis });
		setRun(await commit() && await runOnHost(key, basis.sync_id) ? {
			basis,
			answeredAt: latest().row
		} : void 0);
	};
}
function createArming(ui) {
	const timer = createTimer();
	function disarmAny() {
		timer.stop();
		ui.update({ armed: void 0 });
	}
	function disarm(elementId) {
		if (ui.get().armed?.elementId === elementId) disarmAny();
	}
	function arm(elementId, hint) {
		ui.update({ armed: {
			elementId,
			until: Date.now() + ARMED_MS,
			hint
		} });
		timer.start(ARMED_MS, () => disarm(elementId));
	}
	return {
		arm,
		disarm,
		disarmAny
	};
}
function createCommitQueue(cardStore, latest, { setReturned, setRefusal }, ui) {
	let queue = Promise.resolve(true);
	async function commitEdits() {
		const edits = cardStore.uncommittedEdits();
		if (edits.size === 0) return true;
		let outcome;
		try {
			const changed = editedFields(edits, cardStore.fields());
			if (Object.keys(changed).length === 0) {
				cardStore.markCommitted(edits);
				return true;
			}
			outcome = await latest().onFieldsCommit(changed);
		} catch (error) {
			console.warn("[ACTION_CARDS] saving the card failed", { error });
			ui.update({ saveFailed: true });
			return false;
		}
		ui.update({ saveFailed: false });
		if (isRefusal(outcome)) {
			setRefusal(outcome.validation_errors);
			return false;
		}
		cardStore.markCommitted(edits);
		setRefusal(void 0);
		if (isCardChanged(outcome)) {
			cardStore.reseed(cardState(outcome.card_changed));
			setReturned({
				basis: latest().row,
				row: outcome.card_changed
			});
			return false;
		}
		setReturned({
			basis: latest().row,
			row: outcome
		});
		ui.update({ edited: true });
		return true;
	}
	return () => {
		queue = queue.then(commitEdits);
		return queue;
	};
}
function keepRefusalsOfEditedFields(refusal, cardStore) {
	if (refusal === void 0) return void 0;
	const fields = Object.fromEntries(Object.entries(refusal.fields).filter(([key]) => cardStore.isEdited(`/fields/${key}`)));
	const fieldCount = Object.keys(fields).length;
	if (fieldCount === Object.keys(refusal.fields).length) return refusal;
	if (fieldCount === 0 && refusal.card === null) return void 0;
	return {
		fields,
		card: refusal.card
	};
}
function isRefusal(outcome) {
	return "validation_errors" in outcome;
}
function isCardChanged(outcome) {
	return "card_changed" in outcome;
}
function createUiStore() {
	let ui = {
		saveFailed: false,
		edited: false
	};
	const listeners = /* @__PURE__ */ new Set();
	return {
		get: () => ui,
		update(change) {
			const next = {
				...ui,
				...change
			};
			if (isSameValue(next, ui)) return;
			ui = next;
			for (const listener of listeners) listener();
		},
		subscribe(listener) {
			listeners.add(listener);
			return () => listeners.delete(listener);
		}
	};
}
function createTimer() {
	let timeout;
	return {
		start(ms, then) {
			clearTimeout(timeout);
			timeout = setTimeout(then, ms);
		},
		stop() {
			clearTimeout(timeout);
		}
	};
}
const CardSessionContext = createContext(void 0);
const RESTING_UI = {
	saveFailed: false,
	edited: false
};
const RESTING_CONTROLS = {
	validate: () => true,
	fieldChanged: () => {},
	arm: () => {},
	disarm: () => {},
	focus: () => {},
	blur: () => {},
	retrySave: () => {},
	commitNow: () => {},
	saveEdits: async () => true
};
const subscribeToNothing = () => () => {};
const restingUi = () => RESTING_UI;
function useCardSession() {
	const context = useContext(CardSessionContext);
	const ui = useSyncExternalStore(context?.session.subscribe ?? subscribeToNothing, context?.session.ui ?? restingUi);
	const messages = useValidationMessages();
	return {
		...ui,
		validationCount: messages.length,
		validationMessage: messages.length === 1 ? messages[0] : void 0,
		readOnly: context?.readOnly ?? false,
		hasChecks: context?.session.hasChecks() ?? false,
		...context?.preview
	};
}
function useCardSessionControls() {
	return useContext(CardSessionContext)?.session.controls ?? RESTING_CONTROLS;
}
function useCardStatus() {
	return useStateValue("/status");
}
function useValidationMessages() {
	const refusedFields = useStateValue("/validation_errors/fields");
	const fieldStates = useOptionalValidation()?.fieldStates ?? {};
	const messages = /* @__PURE__ */ new Map();
	for (const [path, fieldState] of Object.entries(fieldStates)) if (fieldState.result?.valid === false) messages.set(path, fieldState.result.errors[0] ?? "");
	for (const [key, message] of Object.entries(refusedFields ?? {})) if (message) messages.set(`/fields/${key}`, message);
	return [...messages.values()];
}
//#endregion
//#region src/react/cardSurface.ts
const CardSurfaceContext = createContext("web_chat");
function useCardSurface() {
	return useContext(CardSurfaceContext);
}
//#endregion
//#region src/react/elementIds.ts
const ELEMENT_ID_PROP = "elementId";
function elementIdOf(props) {
	const elementId = props[ELEMENT_ID_PROP];
	return typeof elementId === "string" ? elementId : void 0;
}
function namespaceElementIds(document, prefix) {
	const elements = Object.fromEntries(Object.entries(document.elements).map(([key, element]) => [key, {
		...element,
		props: {
			...element.props,
			[ELEMENT_ID_PROP]: `${prefix}-${key}`,
			runAction: isRecord(element.on?.press) && element.on.press.action === "run",
			runKey: runKeyOf(element.on?.press)
		}
	}]));
	return {
		...document,
		elements
	};
}
function runKeyOf(press) {
	if (!isRecord(press) || press.action !== "run" || !isRecord(press.params)) return void 0;
	return typeof press.params.key === "string" ? press.params.key : void 0;
}
//#endregion
//#region src/react/CardRenderer.tsx
function CardRenderer(props) {
	return /* @__PURE__ */ jsx(CardView, { ...props }, `${props.row.id}:${props.surface}`);
}
function CardView(props) {
	const { surface, registry, readOnly = false, sessionPreview, saveEditsRef } = props;
	const { row, session } = useCardEditing(props);
	useLayoutEffect(() => {
		if (!saveEditsRef) return;
		saveEditsRef.current = session.commit;
		return () => {
			if (saveEditsRef.current === session.commit) saveEditsRef.current = null;
		};
	}, [saveEditsRef, session]);
	const sessionContext = useMemo(() => ({
		session,
		readOnly,
		preview: sessionPreview
	}), [
		session,
		readOnly,
		sessionPreview
	]);
	const document = useShownDocument(row, surface, props.handlers);
	return /* @__PURE__ */ jsx(CardErrorBoundary, {
		resetKey: row,
		placeholder: /* @__PURE__ */ jsx(CardPlaceholder, { registry }),
		children: /* @__PURE__ */ jsx(JSONUIProvider, {
			registry,
			store: session.store,
			handlers: session.actionHandlers,
			validationFunctions: CHECK_FUNCTIONS,
			children: /* @__PURE__ */ jsx(RegistryContext.Provider, {
				value: registry,
				children: /* @__PURE__ */ jsx(CardSurfaceContext.Provider, {
					value: surface,
					children: /* @__PURE__ */ jsx(CardSessionContext.Provider, {
						value: sessionContext,
						children: /* @__PURE__ */ jsx(CardInteraction, {
							session,
							children: /* @__PURE__ */ jsx(Renderer, {
								spec: document,
								registry,
								fallback: UnknownComponent
							})
						})
					})
				})
			})
		})
	});
}
const INVALID_SHAKE = [
	0,
	-2,
	2,
	-2,
	2,
	0
].map((x) => ({ transform: `translateX(${x}px)` }));
function CardInteraction({ session, children }) {
	const validation = useValidation();
	const { invalidAttempts } = useCardSession();
	const root = useRef(null);
	useLayoutEffect(() => {
		session.setValidator(() => {
			const valid = validation.validateAll();
			const errors = session.store.get("/validation_errors/fields");
			return valid && (errors === void 0 || errors === null || Object.values(errors).every((error) => !error));
		});
	}, [session, validation]);
	useEffect(() => {
		if (!invalidAttempts) return;
		const field = root.current?.querySelector("[aria-invalid=\"true\"]");
		field?.focus();
		if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
		const frame = requestAnimationFrame(() => {
			const focused = document.activeElement;
			(focused && root.current?.contains(focused) ? focused : field)?.animate?.(INVALID_SHAKE, { duration: 160 });
		});
		return () => cancelAnimationFrame(frame);
	}, [invalidAttempts]);
	return /* @__PURE__ */ jsx("div", {
		ref: root,
		style: { display: "contents" },
		onBlur: session.commit,
		onKeyDownCapture: (event) => handleFieldKey(event, root.current),
		children
	});
}
function handleFieldKey(event, root) {
	const target = event.target;
	if (!(target instanceof HTMLElement) || !target.matches("input, textarea, [contenteditable=\"true\"]")) return;
	if (event.key === "Escape") {
		event.preventDefault();
		event.stopPropagation();
		target.blur();
	}
	const modifier = /Mac|iPhone|iPad/.test(navigator.platform) ? event.metaKey : event.ctrlKey;
	if (event.key === "Enter" && modifier && !event.shiftKey && !event.altKey) {
		event.preventDefault();
		event.stopPropagation();
		target.blur();
		root?.querySelector("[data-card-primary=\"true\"]")?.click();
	}
}
function useCardEditing({ row: rowProp, handlers, onFieldsCommit, local }) {
	const [returned, setReturned] = useState();
	const [refusal, setRefusal] = useState();
	const [run, setRun] = useState();
	const row = returned?.basis === rowProp ? returned.row : rowProp;
	const running = isRunning(run, rowProp);
	const seed = useMemo(() => cardState(row, {
		status: running ? "running" : local?.status,
		validation_errors: refusal ?? local?.validation_errors
	}), [
		row,
		running,
		refusal,
		local?.status,
		local?.validation_errors
	]);
	const [session] = useState(() => createCardSession({
		inputs: {
			row: rowProp,
			handlers,
			onFieldsCommit,
			seed
		},
		setReturned,
		setRefusal,
		setRun
	}));
	useLayoutEffect(() => {
		session.setInputs({
			row: rowProp,
			handlers,
			onFieldsCommit,
			seed
		});
	});
	useLayoutEffect(() => session.reseed(seed), [session, seed]);
	return {
		row,
		session
	};
}
function useShownDocument(row, surface, handlers) {
	const incoming = viewFor(row.views, surface);
	const [kept, setKept] = useState(incoming);
	if (incoming !== void 0 && incoming !== kept) setKept(incoming);
	const candidate = incoming ?? kept;
	const check = useMemo(() => candidate === void 0 ? void 0 : checkDocument(candidate), [candidate]);
	useEffect(() => {
		if (check?.ok !== false) return;
		console.warn("[ACTION_CARDS] document failed the check, showing the update prompt", { issues: check.issues });
	}, [check]);
	const provider = providerOf(candidate);
	const updatePrompt = useMemo(() => updatePromptDocument({ provider }), [provider]);
	const shown = check?.ok && isSpec(candidate) ? candidate : updatePrompt;
	const canRestore = handlers.restore !== void 0;
	const handled = useMemo(() => handledActions(canRestore), [canRestore]);
	const drawn = useMemo(() => withoutUnhandledActions(shown, handled), [shown, handled]);
	return useMemo(() => namespaceElementIds(drawn, `card-${row.id}-${surface}`), [
		drawn,
		row.id,
		surface
	]);
}
const RegistryContext = createContext({});
function UnknownComponent({ element }) {
	const registry = useContext(RegistryContext);
	const placeholder = useMemo(() => placeholderFor(element.type), [element.type]);
	return /* @__PURE__ */ jsx(Renderer, {
		spec: placeholder,
		registry
	});
}
function CardPlaceholder({ registry }) {
	const placeholder = useMemo(() => placeholderFor("card"), []);
	return /* @__PURE__ */ jsx(JSONUIProvider, {
		registry,
		children: /* @__PURE__ */ jsx(Renderer, {
			spec: placeholder,
			registry
		})
	});
}
function providerOf(document) {
	const provider = (isSpec(document) ? Object.values(document.elements).find((element) => element.type === "ProviderIcon" && typeof element.props.provider === "string") : void 0)?.props.provider;
	return typeof provider === "string" ? provider : "littlebird";
}
//#endregion
export { CHECK_FUNCTIONS, CardErrorBoundary, CardRenderer, defineRegistry, elementIdOf, isCardChanged, isEmailAddress, isRefusal, passEmptyPatterns, useBoundProp, useCardSession, useCardSessionControls, useCardStatus, useCardSurface, useFieldValidation, useStateValue };

//# sourceMappingURL=index.js.map